import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { NAV_GROUPS, NAV_LINKS } from '../data/navMenu';
import { useAccount } from '../account/AccountContext.jsx';
import CrownIcon from './CrownIcon.jsx';
import './NavBar.css';

function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [gamesOpen, setGamesOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();
  const gamesRef = useRef(null);
  // Spark & Spiral account: "Sign in" until a session is known (so the
  // prerendered HTML matches the first render), then "Account" or "Plus".
  const { user, member } = useAccount();
  const accountLabel = user ? (member ? 'Plus' : 'Account') : 'Sign in';

  // Close everything on navigation.
  useEffect(() => {
    setMenuOpen(false);
    setGamesOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  // Close the desktop mega-menu on outside click or Escape.
  useEffect(() => {
    if (!gamesOpen) return undefined;
    const onDown = (e) => {
      if (gamesRef.current && !gamesRef.current.contains(e.target)) setGamesOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setGamesOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [gamesOpen]);

  useEffect(() => {
    document.body.classList.toggle('nav-locked', menuOpen);
    return () => document.body.classList.remove('nav-locked');
  }, [menuOpen]);

  const isGamesRoute = /^\/(games|play|logic-games|word-games|card-games|printable)/.test(location.pathname);

  return (
    <header className={`site-nav${menuOpen ? ' is-open' : ''}`}>
      <div className="container site-nav__bar">
        <Link to="/" className="site-nav__brand" aria-label="Logic Puzzles home">
          <img src="/brand/mark.png" alt="" width="34" height="34" />
          <span className="site-nav__name">
            Logic Puzzles
            <small>by Spark &amp; Spiral</small>
          </span>
        </Link>

        <nav className="site-nav__desktop" aria-label="Main">
          <div className={`mega${gamesOpen ? ' is-open' : ''}`} ref={gamesRef}>
            <button
              type="button"
              className={`site-nav__link mega__trigger${isGamesRoute ? ' active' : ''}`}
              aria-expanded={gamesOpen}
              aria-controls="mega-panel"
              onClick={() => setGamesOpen((o) => !o)}
            >
              Games
              <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className="mega__panel" id="mega-panel">
              <div className="mega__grid">
                {NAV_GROUPS.map((group) => (
                  <div key={group.title} className={`mega__col${group.wide ? ' mega__col--wide' : ''}`}>
                    <p className="mega__title">
                      {group.all ? <Link to={group.all.to}>{group.title}</Link> : group.title}
                    </p>
                    <ul>
                      {group.links.map((l) => (
                        <li key={l.to}>
                          <Link to={l.to}>{l.label}</Link>
                        </li>
                      ))}
                    </ul>
                    {group.all && (
                      <Link to={group.all.to} className="mega__all">
                        {group.all.label} →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/crowns-puzzle" className="site-nav__link site-nav__crowns">
            <span aria-hidden="true"><CrownIcon /></span> Daily Crowns
          </NavLink>
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className="site-nav__link">
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/account"
            className={`site-nav__link site-nav__account${member ? ' is-member' : ''}`}
            title={member ? 'Spark & Spiral Plus: ad-free while signed in' : undefined}
          >
            {accountLabel}
          </NavLink>
          <Link to="/logic-games" className="button site-nav__cta">
            Play free
          </Link>
        </nav>

        <button
          type="button"
          className="site-nav__toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span />
          <span />
        </button>
      </div>

      <div className="site-nav__mobile" id="mobile-menu" hidden={!menuOpen}>
        <div className="container">
          <Link to="/crowns-puzzle" className="mobile-feature">
            <span aria-hidden="true"><CrownIcon /></span>
            <span>
              <strong>Daily Crowns</strong>
              <small>Today’s free puzzle, plus unlimited play</small>
            </span>
          </Link>
          {NAV_GROUPS.map((group) => {
            const open = openGroup === group.title;
            return (
              <div key={group.title} className={`mobile-group${open ? ' is-open' : ''}`}>
                <button type="button" aria-expanded={open} onClick={() => setOpenGroup(open ? null : group.title)}>
                  {group.title}
                  <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                {open && (
                  <ul>
                    {group.all && (
                      <li>
                        <Link to={group.all.to} className="mobile-group__all">{group.all.label}</Link>
                      </li>
                    )}
                    {group.links.map((l) => (
                      <li key={l.to}>
                        <Link to={l.to}>{l.label}</Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          <div className="mobile-links">
            {[
              ...NAV_LINKS,
              { label: 'FAQ', to: '/faq' },
              { label: 'Contact', to: '/contact' },
              { label: member ? 'Spark & Spiral Plus account' : accountLabel, to: '/account' },
            ].map((l) => (
              <Link key={l.to} to={l.to}>{l.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

export default NavBar;
